import { Search, Phone, Menu } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/company-logo.png";

export function Header({ simple = false }: { simple?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  const navItems: { name: string; path: string; external?: boolean }[] = [
    { name: "首页", path: "/" },
    { name: "项目公告信息", path: "/trade-announcements" },
    { name: "企业新闻", path: "/company-culture" },
    { name: "资料下载", path: "/downloads" },
    { name: "关于我们", path: "/about" },
    { name: "供应商登录", path: "/my-bids" ,external: true},
  ];

  const handleNavClick = (path: string, external?: boolean) => {
    if (path !== "#") {
      if (external) {
        window.open(path, "_blank", "noopener noreferrer");
      } else {
        navigate(path);
      }
    }
  };

  return (
    <header id="site-header" className="text-white shadow-lg">
      {/* Desktop Top Bar */}
      <div className="hidden md:block relative border-b border-white/20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #4A4A4A 0%, #6B6B6B 50%, #8A8A8A 100%)' }}>
        {/* Gray gradient background */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(135deg, #4A4A4A 0%, #5A5A5A 50%, #6B6B6B 100%)'
          }}
        ></div>
        {/* Gold accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#C9A227] to-transparent"></div>
        <div className="container mx-auto px-4 py-4 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={logo}
                alt="德润慧通"
                className="h-20 w-auto cursor-pointer"                
                onClick={() => navigate('/')}
                style={{ 
                  height: "80px", 
                  margin: "0 auto",
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.98
                }}
              />
            </div>
            <div className="flex flex-col items-end space-y-1 text-white">
              <div className="flex items-center">
                <span
                  className="text-2xl font-bold tracking-wide"
                 style={{ color: '#ffffff' }}>
                  德正润业·慧达通规
                </span>
              </div>
              <div className="flex items-center space-x-2 text-base">
                <h4 className="text-white font-semibold" style={{ color: '#ffffff' }}>
                  服务热线：<span style={{ fontWeight: 700 }}>028-62073616</span>
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden bg-gradient-to-r from-[#4A4A4A] to-[#6B6B6B] text-white px-3 py-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            {!simple && (
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="flex-shrink-0 h-14 w-14">
                    <Menu className="h-8 w-8 text-gray-700" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] p-0" style={{ background: 'linear-gradient(180deg, #1A1A2E 0%, #2D2D44 100%)' }}>
                  <SheetTitle className="sr-only">导航菜单</SheetTitle>
                  <SheetDescription className="sr-only">网站导航菜单，选择要访问的页面</SheetDescription>
                  <div className="px-6 py-8" style={{ background: 'linear-gradient(135deg, #1A1A2E 0%, #B8860B 100%)' }}>
                    <h3 className="text-white text-lg">导航菜单</h3>
                  </div>
                  <nav className="p-4">
                    <ul className="space-y-1">
                      {navItems.map((item, index) => (
                        <li key={index}>
                          <a
                            href={item.external ? item.path : "#"}
                            target={item.external ? "_blank" : "_self"}
                            rel={item.external ? "noopener noreferrer" : ""}
                            onClick={(e) => {
                              if (!item.external) {
                                e.preventDefault();
                                setIsOpen(false);
                                handleNavClick(item.path, item.external);
                              }
                            }}
                            className={`block px-4 py-3 rounded-lg transition-colors ${
                              index === 0 ? "text-[#C9A227] font-semibold" : "text-white hover:text-[#C9A227] hover:bg-white/10"
                            }`}
                          >
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            
            {/* Logo */}
            <img
              src={logo}
              alt="德润慧通"
              className="h-14 w-auto cursor-pointer"
              onClick={() => navigate('/')}
              style={{ 
                filter: 'brightness(0) invert(1)',
                opacity: 0.98
              }}
            />
          </div>
          
          {/* Spacer to push right content to the right */}
          <div className="flex-1"></div>
          
          <div className="flex flex-col items-end gap-1 text-xs flex-shrink-0">
            <span className="text-gray-700 whitespace-nowrap" style={{ fontFamily: "'LiSu', 'SimLi', 'KaiTi', serif", fontSize: "16px" }}>
              德正润业·慧达通规
            </span>
            <span className="text-gray-900 whitespace-nowrap" style={{ fontSize: "12px" }}>
              服务热线：<span style={{ fontWeight: 700 }}>028-62073616</span>
            </span>
          </div>
        </div>
      </div>

      {!simple && (
        <nav className="hidden md:block relative" style={{ background: 'linear-gradient(90deg, #4A4A4A 0%, #5A5A5A 100%)' }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <ul className="flex items-center space-x-1">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <a
                      href={item.external ? item.path : "#"}
                      target={item.external ? "_blank" : "_self"}
                      rel={item.external ? "noopener noreferrer" : ""}
                      onClick={(e) => {
                        if (!item.external) {
                          e.preventDefault();
                          handleNavClick(item.path, item.external);
                        }
                      }}
                      className={`block px-6 py-4 transition-colors cursor-pointer ${
                        index === 0 
                          ? "text-[#C9A227] font-semibold border-b-2 border-[#C9A227]" 
                          : "text-white hover:text-[#C9A227] hover:bg-white/5"
                      }`}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="relative flex items-center w-80" style={{ display: 'none' }}>
                <Input
                  placeholder="请输入关键字搜索..."
                  className="bg-white border-0 h-10 pr-20 placeholder:text-gray-400 text-gray-900"
                />
                <Button
                  size="sm"
                  className="absolute right-1 top-1 bottom-1 bg-[#B8860B] hover:bg-[#0050b8] text-white"
                >
                  <Search className="w-4 h-4 mr-1" />
                  搜索
                </Button>
              </div>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}